import { LocationProvider, Route, Router, useLocation } from 'preact-iso';
import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';
import { Nav } from './components/Nav';
import { surfaceFor } from './lib/routes';
import { Footer } from './sections/Footer';
import { Home } from './routes/Home';
import { DetailsPage } from './routes/Details';
import { OurStory } from './routes/OurStory';
import { Travel } from './routes/Travel';
import { RsvpFind } from './routes/RsvpFind';
import { Afterparty } from './routes/Afterparty';
import { Admin } from './routes/Admin';
import { NotFound } from './routes/NotFound';

/**
 * Where the guest list lives. Unlisted by default and overridable at build
 * time (`VITE_ADMIN_PATH`), so the path need not appear in a public repo — but
 * the page is only a shell either way. The Worker is what enforces access.
 */
export const ADMIN_PATH = import.meta.env.VITE_ADMIN_PATH?.trim() || '/admin';

/**
 * Paints the document itself, not just the page content.
 *
 * iOS tints the status bar and the toolbar strips from the document
 * background and the theme colour rather than from whatever element happens
 * to lie beneath them, so a forest page framed by a paper `body` shows two
 * pale bands. Follow the route on both.
 *
 * The token per surface is written out rather than reached through an alias,
 * because a custom property whose value is another `var()` computes to the
 * unsubstituted token stream: `getPropertyValue` on it hands back the literal
 * text `var(--forest)`, which is not a colour. These have to resolve to one.
 */
const SURFACE_TOKEN = {
  paper: '--paper',
  forest: '--forest',
  night: '--forest-deep',
} as const;

function Surface() {
  const { path } = useLocation();
  const surface = surfaceFor(path);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.surface = surface;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      const colour = getComputedStyle(root)
        .getPropertyValue(SURFACE_TOKEN[surface])
        .trim();
      if (colour) meta.setAttribute('content', colour);
    }
  }, [surface]);

  return null;
}

/**
 * The router pushes state without the browser's native anchor handling, so
 * restore both behaviours by hand: honour a hash if there is one, otherwise
 * start the new page at the top.
 */
function ScrollManager() {
  const { url } = useLocation();

  useEffect(() => {
    const hash = url.includes('#') ? url.slice(url.indexOf('#') + 1) : '';
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    // Wait a frame so the destination route has actually rendered.
    const id = requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [url]);

  return null;
}

/** The guest list is a working tool, not part of the invitation — no nav, no footer. */
function Frame({ children }: { children: ComponentChildren }) {
  const { path } = useLocation();
  if (path === ADMIN_PATH) return <>{children}</>;
  return (
    <>
      <Nav />
      {children}
      <Footer />
    </>
  );
}

export function App() {
  return (
    <LocationProvider>
      <ScrollManager />
      <Surface />
      <Frame>
        <Router>
          <Route path="/" component={Home} />
          <Route path="/details" component={DetailsPage} />
          <Route path="/our-story" component={OurStory} />
          <Route path="/travel" component={Travel} />
          <Route path="/rsvp" component={RsvpFind} />
          <Route path="/afterparty" component={Afterparty} />
          <Route path={ADMIN_PATH} component={Admin} />
          <Route default component={NotFound} />
        </Router>
      </Frame>
    </LocationProvider>
  );
}
